import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, Icon, Select, Spinner, useToast } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { brl, datePt, todayIso, addDaysIso } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface Totals {
  appointments: number; completed: number; cancelled: number; noShow: number;
  pending: number; confirmed: number; estimatedCents: number; receivedCents: number;
}
interface Report {
  period: { from: string; to: string };
  totals: Totals;
  note: string;
  topServices: { name: string; bookings: number; estimatedCents: number }[];
  peakHours: { hour: string; bookings: number }[];
  recurringCustomers: { name: string; visits: number; estimatedCents: number }[];
  perProfessional: { name: string; appointments: number; completed: number; cancelled: number; noShow: number; estimatedCents: number }[];
  cancellationHistory: { code: string; serviceName: string; scheduledDate: string; status: string; reason: string | null; at: string; by: string | null }[];
}

interface Fail { status: number; message: string }

function toFail(err: unknown): Fail {
  if (err instanceof ApiError) return { status: err.status, message: err.message };
  return { status: 0, message: err instanceof Error ? err.message : 'Não foi possível concluir a operação.' };
}

const firstOfMonth = (): string => `${todayIso().slice(0, 7)}-01`;

export function PanelReportsPage(): JSX.Element {
  usePageMeta(tr('Relatórios'), tr('Indicadores de agendamentos e exportação CSV.'), '/painel/relatorios');
  const toast = useToast();
  const { active } = useAuth();
  const bid = active?.businessId ?? '';

  const [from, setFrom] = useState<string>(addDaysIso(todayIso(), -29));
  const [to, setTo] = useState<string>(todayIso());
  const [preset, setPreset] = useState<string>('last30');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Fail | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [paymentConfigured, setPaymentConfigured] = useState<boolean | null>(null);
  const [pixEnabled, setPixEnabled] = useState(false);

  const applyPreset = (value: string): void => {
    setPreset(value);
    const today = todayIso();
    if (value === 'month') { setFrom(firstOfMonth()); setTo(today); }
    else if (value === 'last30') { setFrom(addDaysIso(today, -29)); setTo(today); }
    else if (value === 'last90') { setFrom(addDaysIso(today, -89)); setTo(today); }
    else if (value === 'year') { setFrom(`${today.slice(0, 4)}-01-01`); setTo(today); }
  };

  const load = useCallback(async (): Promise<void> => {
    if (!bid) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<Report>(`/api/reports/${bid}/reports?from=${from}&to=${to}`);
      setReport(r);
    } catch (err) {
      setError(toFail(err));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [bid, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!bid) return;
    let cancelled = false;
    api.get<{ paymentConfigured: boolean; pixEnabled: boolean }>(`/api/subscriptions/${bid}`)
      .then((s) => { if (!cancelled) { setPaymentConfigured(s.paymentConfigured); setPixEnabled(Boolean(s.pixEnabled)); } })
      .catch(() => { if (!cancelled) setPaymentConfigured(null); });
    return () => { cancelled = true; };
  }, [bid]);

  const maxHour = report ? Math.max(1, ...report.peakHours.map((h) => h.bookings)) : 1;
  const csvHref = `/api/reports/${bid}/reports/export.csv?from=${from}&to=${to}`;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Relatórios")}</h1>
          <p className="text-sm text-muted">{tr("Indicadores do período selecionado.")}</p>
        </div>
        <a
          href={report ? csvHref : undefined}
          onClick={(e) => {
            if (!report) { e.preventDefault(); return; }
            toast('Baixando CSV…', 'info');
          }}
          className={`nl-btn nl-btn-secondary ${report ? '' : 'pointer-events-none opacity-60'}`}
          aria-disabled={!report}
        >
          <Icon name="chart" /> {tr("Exportar CSV")}
        </a>
      </div>

      <Card as="section">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="nl-label">{tr("Período")}</span>
            <Select value={preset === 'custom' ? 'custom' : preset} onChange={(e) => applyPreset(e.target.value)}>
              <option value="month">{tr("Este mês")}</option>
              <option value="last30">{tr("Últimos 30 dias")}</option>
              <option value="last90">{tr("Últimos 90 dias")}</option>
              <option value="year">{tr("Este ano")}</option>
              {preset === 'custom' ? <option value="custom">{tr("Personalizado")}</option> : null}
            </Select>
          </label>
          <label className="block">
            <span className="nl-label">{tr("De")}</span>
            <input
              type="date"
              className="nl-input"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }}
            />
          </label>
          <label className="block">
            <span className="nl-label">{tr("Até")}</span>
            <input
              type="date"
              className="nl-input"
              value={to}
              onChange={(e) => { setTo(e.target.value); setPreset('custom'); }}
            />
          </label>
          <Button variant="secondary" onClick={() => void load()} loading={loading}>{tr("Aplicar")}</Button>
        </div>
        {from > to ? <Alert tone="warning" title={tr("Período inválido")}>{tr("A data inicial é posterior à final.")}</Alert> : null}
      </Card>

      {loading ? (
        <Card><Spinner label={tr("Calculando relatório…")} /></Card>
      ) : error ? (
        <Alert tone={error.status === 402 || error.status === 403 ? 'warning' : 'danger'} title={error.status === 402 ? tr('Recurso indisponível no plano') : error.status === 403 ? tr('Permissão insuficiente') : tr('Erro')}>
          <p>{error.message}</p>
          {error.status === 402 ? <p className="mt-1">{tr("Relatórios e exportação exigem plano compatível. Veja")} <Link className="font-semibold underline" to="/painel/plano">{tr("Meu plano")}</Link>.</p> : null}
        </Alert>
      ) : !report ? (
        <EmptyState title={tr("Sem dados")} description={tr("Selecione um período válido para gerar o relatório.")} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label={tr("Agendamentos")} value={String(report.totals.appointments)} />
            <Metric label={tr("Concluídos")} value={String(report.totals.completed)} />
            <Metric label={tr("Cancelados")} value={String(report.totals.cancelled)} />
            <Metric label={tr("Não compareceram")} value={String(report.totals.noShow)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <MoneyCard
              title={tr("Valor estimado dos atendimentos")}
              caption={tr("Soma do preço registrado nos agendamentos concluídos e confirmados. É uma estimativa, não um recebimento.")}
              cents={report.totals.estimatedCents}
            />
            <MoneyCard
              title={tr("Recebido (confirmado pelas cobranças)")}
              caption={
                paymentConfigured
                  ? tr('Valores efetivamente pagos, vindos das cobranças confirmadas pelo provedor de pagamento.')
                  : tr('Valores efetivamente recebidos, registrados na confirmação do comprovante pela administração.')
              }
              cents={report.totals.receivedCents}
              zeroNote={
                report.totals.receivedCents === 0 && paymentConfigured === false
                  ? pixEnabled
                    ? tr('O recebimento aparece aqui depois que a administração confere o comprovante do Pix e ativa a licença. Nada é confirmado automaticamente.')
                    : tr('O provedor de pagamento ainda não está configurado, por isso nenhum recebimento é confirmado aqui. Os valores acima permanecem estimados.')
                  : undefined
              }
            />
          </div>

          <Alert tone="info" title={tr("Como ler os números")}>{tr(report.note)}</Alert>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card as="section">
              <h2 className="mb-3 text-lg font-semibold">{tr("Serviços mais agendados")}</h2>
              {report.topServices.length === 0 ? <EmptyState title={tr("Nenhum serviço no período")} /> : (
                <ul className="space-y-2 text-sm">
                  {report.topServices.map((s, i) => (
                    <li key={`${s.name}-${i}`} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate">{s.name}</span>
                      <span className="shrink-0 text-muted">{s.bookings}× · {brl(s.estimatedCents)} <span className="text-[0.75rem]">{tr("(estimado)")}</span></span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card as="section">
              <h2 className="mb-3 text-lg font-semibold">{tr("Horários de pico")}</h2>
              {report.peakHours.length === 0 ? <EmptyState title={tr("Sem movimentação")} /> : (
                <div className="space-y-1.5">
                  {report.peakHours.map((h) => (
                    <div key={h.hour} className="flex items-center gap-2 text-sm">
                      <span className="w-12 shrink-0 tabular-nums">{h.hour}{tr("h")}</span>
                      <span className="h-3 rounded-sm bg-sage" style={{ width: `${(h.bookings / maxHour) * 100}%`, minWidth: '0.5rem' }} aria-hidden="true" />
                      <span className="text-muted">{h.bookings}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <Card as="section">
            <h2 className="mb-3 text-lg font-semibold">{tr("Desempenho por profissional")}</h2>
            {report.perProfessional.length === 0 ? <EmptyState title={tr("Nenhum profissional com atendimentos no período")} /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-muted"><th className="py-1">{tr("Profissional")}</th><th>{tr("Agend.")}</th><th>{tr("Concl.")}</th><th>{tr("Cancel.")}</th><th>{tr("Faltas")}</th><th>{tr("Estimado")}</th></tr></thead>
                  <tbody>
                    {report.perProfessional.map((p, i) => (
                      <tr key={`${p.name}-${i}`} className="border-t border-line">
                        <td className="py-1.5">{p.name}</td>
                        <td>{p.appointments}</td><td>{p.completed}</td><td>{p.cancelled}</td><td>{p.noShow}</td>
                        <td>{brl(p.estimatedCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card as="section">
              <h2 className="mb-3 text-lg font-semibold">{tr("Clientes recorrentes")}</h2>
              {report.recurringCustomers.length === 0 ? <EmptyState title={tr("Nenhum cliente recorrente")} /> : (
                <ul className="space-y-2 text-sm">
                  {report.recurringCustomers.map((c, i) => (
                    <li key={`${c.name}-${i}`} className="flex justify-between gap-2">
                      <span className="min-w-0 truncate">{c.name}</span>
                      <span className="shrink-0 text-muted">{c.visits} {tr("visitas ·")} {brl(c.estimatedCents)} {tr("(estimado)")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card as="section">
              <h2 className="mb-3 text-lg font-semibold">{tr("Histórico de cancelamentos")}</h2>
              {report.cancellationHistory.length === 0 ? <EmptyState title={tr("Nenhum cancelamento no período")} /> : (
                <ul className="space-y-2 text-sm">
                  {report.cancellationHistory.slice(0, 15).map((c) => (
                    <li key={c.code} className="flex flex-col border-b border-line pb-1.5">
                      <span className="flex justify-between gap-2">
                        <span className="min-w-0 truncate">{c.serviceName} · {datePt(c.scheduledDate)}</span>
                        <span className="shrink-0 text-muted">{c.code}</span>
                      </span>
                      <span className="text-xs text-muted">{c.status}{c.reason ? ` — ${c.reason}` : ''}{c.by ? ` (por ${c.by})` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Card as="section" className="p-5">
      <p className="text-sm text-muted">{label}</p>
      <p className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">{value}</p>
    </Card>
  );
}

function MoneyCard({ title, caption, cents, zeroNote }: { title: string; caption: string; cents: number; zeroNote?: string }): JSX.Element {
  return (
    <Card as="section">
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{brl(cents)}</p>
      <p className="nl-hint mt-1">{caption}</p>
      {zeroNote ? <div className="mt-2"><Alert tone="warning" title={tr("Sem recebimentos confirmados")}>{zeroNote}</Alert></div> : null}
    </Card>
  );
}
